'use client';

import { useState, useEffect } from 'react';
import { SectionHeader } from '@church/nextjs-ui/components/SectionHeader';
import type {
  ChurchEvent,
  RecurringSeries,
  StandaloneOpportunity,
  SermonSeries,
  Announcement,
  BaptismInfo,
  LifeGroupsInfo,
  ImNewInfo,
} from '@/types/serve-finder';
import { EventGrid } from './EventGrid';
import { CurrentSeriesCard } from './CurrentSeriesCard';
import { WoodsideIcon } from './WoodsideIcon';
import { RotatingSubtitle } from './RotatingSubtitle';

// ---------------------------------------------------------------------------
// Mock Data — Events
// ---------------------------------------------------------------------------

const MOCK_EVENTS: ChurchEvent[] = [
  // ── Hope 365 ────────────────────────────────────────────────────────────
  {
    ID: 300,
    Title: '',
    Subtitle: null,
    Detail_Heading: 'ONE WEEKEND,\nENDLESS HOPE',
    Detail_Heading_Tracking: '-0.09em',
    Detail_Image_URL: '/Hope-365/what-to-expect-image.png',
    Detail_Video_URL: '/Hope-365/what-to-expect-video.mp4',
    Description:
      "It's more than an event. It's a movement of students. Hope 365 Conference is a discipleship pathway that will challenge students to take the next step in their faith.",
    Page_URL: 'https://woodsidebible.org/hope-365/',
    BG_URL: '/Hope-365/BGVideo.mp4',
    BG_Is_Video: true,
    FG_URL: '/Hope-365/ForegroundImage.png',
    Congregation_Name: 'Troy Campus',
    Ministry_Name: 'Student Ministry',
    Start_Date: 'Jul 17, 2026',
    End_Date: 'Jul 19, 2026',
    Registration_URL: null,
    Registration_Label: null,
    Sessions: [],
    Serving_Roles: [],
    Registration_Options: [
      {
        Label: 'Go to Conference',
        Description: 'Register as a student for the full 3-day experience',
        URL: '#',
        Icon: 'attend' as const,
      },
      {
        Label: 'Serve',
        Description: 'Serve on the team that makes Hope 365 happen',
        URL: '#',
        Icon: 'serve' as const,
      },
      {
        Label: 'Concert Tickets',
        Description: 'Purchase tickets for the closing night concert',
        URL: '#',
        Icon: 'ticket' as const,
      },
    ],
    Smart_Registration: null,
    Content_Slides: [
      {
        Image_URL: null,
        BG_Image: '/Hope-365/what-to-expect-teal-background.jpg',
        Label: null,
        Heading: "It's a Movement",
        Blurb: '',
        BG_Color: null,
      },
      {
        Image_URL: null,
        BG_Image: null,
        Label: 'Tickets',
        Heading: 'Available Through July 10',
        Blurb: 'Early Bird $149 | Normal $159',
        BG_Color: '#9b30d4',
      },
      {
        Image_URL: '/Hope-365/speaker.png',
        BG_Image: null,
        Label: 'Speaker',
        Heading: 'Jerrod Gunter',
        Blurb: 'Founder: Riotstarter',
        BG_Color: '#e4d5c7',
      },
      {
        Image_URL: '/Hope-365/singer.png',
        BG_Image: null,
        Label: 'Worship',
        Heading: 'Taya',
        Blurb: 'Saturday Night Concert',
        BG_Color: '#9b30d4',
      },
      {
        Image_URL: '/Hope-365/singer2.png',
        BG_Image: null,
        Label: 'Worship',
        Heading: 'Hulvey',
        Blurb: 'Saturday Night Concert',
        BG_Color: '#FD4300',
      },
    ],
    Card_Theme: {
      Body_BG: '#1a1a1a',
      Body_BG_Image: '/Hope-365/pageBG.jpg',
      Title_Color: '#FD4300',
      Subtitle_Color: '#E7DBC3',
      Body_Color: '#E7DBC3',
      Accent_Color: '#FD4300',
      Border_Color: '#333333',
    },
  },

  // ── Christmas at Woodside ───────────────────────────────────────────────
  {
    ID: 400,
    Title: 'Christmas at Woodside Bible',
    Subtitle: null,
    Detail_Heading: null,
    Description: 'Invite your friends and family to celebrate Christmas at Woodside!',
    Page_URL: 'https://woodsidebible.org/christmas/',
    BG_URL: '/Christmas/BGImage.png',
    BG_Is_Video: false,
    FG_URL: '/Christmas/ForegroundImage.png',
    Congregation_Name: 'All Campuses',
    Ministry_Name: 'Christmas',
    Start_Date: 'Dec 23, 2026',
    End_Date: 'Dec 24, 2026',
    Registration_URL: null,
    Registration_Label: 'RSVP',
    Sessions: [
      {
        ID: 401,
        Label: 'Lake Orion',
        Dates: 'Tue, Dec 23 & Wed, Dec 24',
        Campuses: ['Lake Orion'],
        Registration_URL: '#',
        Service_Times: [
          {
            Time: '1:00 PM',
            Day: 'Tue, Dec 23',
            Amenities: ['baby'],
            RSVP_URL: '#',
            Capacity_Pct: 20,
          },
          {
            Time: '4:00 PM',
            Day: 'Tue, Dec 23',
            Amenities: ['baby', 'heart'],
            RSVP_URL: '#',
            Capacity_Pct: 28,
          },
          {
            Time: '7:00 PM',
            Day: 'Tue, Dec 23',
            Amenities: ['baby', 'asl'],
            RSVP_URL: '#',
            Capacity_Pct: 18,
          },
          {
            Time: '11:00 AM',
            Day: 'Wed, Dec 24',
            Amenities: ['baby'],
            RSVP_URL: '#',
            Capacity_Pct: 26,
          },
          {
            Time: '1:00 PM',
            Day: 'Wed, Dec 24',
            Amenities: ['baby', 'heart'],
            RSVP_URL: '#',
            Capacity_Pct: 22,
          },
          {
            Time: '2:30 PM',
            Day: 'Wed, Dec 24',
            Amenities: ['baby', 'asl'],
            RSVP_URL: '#',
            Capacity_Pct: 25,
          },
          {
            Time: '4:00 PM',
            Day: 'Wed, Dec 24',
            Amenities: ['baby'],
            RSVP_URL: '#',
            Capacity_Pct: 22,
          },
        ],
        Serving_Roles: [],
        Extras: [
          {
            Label: 'Christmas Party',
            Description: 'Join us for a festive evening of food, fun, and community.',
            URL: '#',
            Image_URL: '/Christmas/ChristmasParty.jpg',
            Icon: 'party' as const,
          },
          {
            Label: 'Watch Online',
            Description: "Can't make it in person? Stream the Christmas Eve service live.",
            URL: '#',
            Image_URL: null,
            Icon: 'video' as const,
          },
        ],
      },
      {
        ID: 402,
        Label: 'Troy',
        Dates: 'Tue, Dec 23 & Wed, Dec 24',
        Campuses: ['Troy'],
        Registration_URL: '#',
        Service_Times: [
          {
            Time: '1:00 PM',
            Day: 'Tue, Dec 23',
            Amenities: ['baby', 'heart'],
            RSVP_URL: '#',
            Capacity_Pct: 22,
          },
          {
            Time: '4:00 PM',
            Day: 'Tue, Dec 23',
            Amenities: ['baby'],
            RSVP_URL: '#',
            Capacity_Pct: 35,
          },
          {
            Time: '7:00 PM',
            Day: 'Tue, Dec 23',
            Amenities: ['baby'],
            RSVP_URL: '#',
            Capacity_Pct: 25,
          },
          {
            Time: '1:00 PM',
            Day: 'Wed, Dec 24',
            Amenities: ['baby'],
            RSVP_URL: '#',
            Capacity_Pct: 22,
          },
          {
            Time: '4:00 PM',
            Day: 'Wed, Dec 24',
            Amenities: ['baby', 'heart', 'asl'],
            RSVP_URL: '#',
            Capacity_Pct: 42,
          },
          {
            Time: '7:00 PM',
            Day: 'Wed, Dec 24',
            Amenities: ['baby', 'heart'],
            RSVP_URL: '#',
            Capacity_Pct: 23,
          },
          { Time: '11:00 PM', Day: 'Wed, Dec 24', Amenities: [], RSVP_URL: '#', Capacity_Pct: 22 },
        ],
        Serving_Roles: [],
        Extras: [
          {
            Label: 'Christmas Party',
            Description: 'Join us for a festive evening of food, fun, and community.',
            URL: '#',
            Image_URL: '/Christmas/ChristmasParty.jpg',
            Icon: 'party' as const,
          },
          {
            Label: 'Watch Online',
            Description: "Can't make it in person? Stream the Christmas Eve service live.",
            URL: '#',
            Image_URL: null,
            Icon: 'video' as const,
          },
        ],
      },
      {
        ID: 403,
        Label: 'Rochester',
        Dates: 'Wed, Dec 24',
        Campuses: ['Rochester'],
        Registration_URL: '#',
        Service_Times: [
          {
            Time: '1:00 PM',
            Day: 'Wed, Dec 24',
            Amenities: ['baby'],
            RSVP_URL: '#',
            Capacity_Pct: 18,
          },
          {
            Time: '4:00 PM',
            Day: 'Wed, Dec 24',
            Amenities: ['baby'],
            RSVP_URL: '#',
            Capacity_Pct: 30,
          },
          {
            Time: '7:00 PM',
            Day: 'Wed, Dec 24',
            Amenities: ['baby'],
            RSVP_URL: '#',
            Capacity_Pct: 15,
          },
        ],
        Serving_Roles: [],
        Extras: [
          {
            Label: 'Christmas Party',
            Description: 'Join us for a festive evening of food, fun, and community.',
            URL: '#',
            Image_URL: '/Christmas/ChristmasParty.jpg',
            Icon: 'party' as const,
          },
          {
            Label: 'Watch Online',
            Description: "Can't make it in person? Stream the Christmas Eve service live.",
            URL: '#',
            Image_URL: null,
            Icon: 'video' as const,
          },
        ],
      },
    ],
    Serving_Roles: [],
    Registration_Options: null,
    Smart_Registration: null,
    Content_Slides: null,
    Card_Theme: {
      Body_BG: '#1a2a1a',
      Body_BG_Image: '/Christmas/BGImage.png',
      Title_Color: '#ffffff',
      Subtitle_Color: '#FBFCD2',
      Body_Color: 'rgba(255,255,255,0.8)',
      Accent_Color: '#FBFCD2',
      Border_Color: 'rgba(255,255,255,0.1)',
    },
  },

  // ── Shine On ────────────────────────────────────────────────────────────
  {
    ID: 200,
    Title: 'Shine On 2026',
    Subtitle: 'This is Me!',
    Detail_Heading: 'A PROM FOR\nEVERYONE',
    Collapsible_Header: false,
    Detail_BG: '#CEB895',
    Detail_Color: '#032C3F',
    Detail_Body_Color: '#032C3F',
    Detail_Image_URL: '/Shine-On/description_image.jpg',
    Description:
      'Every spring, our special needs ministry hosts Shine On, a prom for adults with disabilities. This April, guests will experience a dazzling afternoon that includes a red-carpet welcome, dancing, karaoke, bingo, a delicious meal, and more! At least 400 volunteers are needed to create the best experience for the kings and queens in attendance.',
    Page_URL: 'https://woodsidebible.org/shine-on/',
    BG_URL: '/Shine-On/Image.jpg',
    BG_Is_Video: false,
    FG_URL: null,
    Congregation_Name: 'Troy Campus',
    Ministry_Name: 'Special Needs Ministry',
    Start_Date: 'Apr 25, 2026',
    End_Date: null,
    Registration_URL: null,
    Registration_Label: null,
    Sessions: [],
    Registration_Options: [
      {
        Label: 'Attend as a Guest',
        Description: 'Register to attend the prom as a guest with special needs',
        URL: '#',
        Icon: 'attend' as const,
      },
      {
        Label: 'Volunteer',
        Description: '37 volunteers needed',
        URL: '',
        Icon: 'serve' as const,
      },
    ],
    Serving_Roles: [
      {
        ID: 3,
        Title: 'Decorating Team',
        Description: 'Transform the venue into a magical prom experience.',
        Image_URL: null,
        Time: '2:00 PM',
        Sign_Up_URL: '#',
        Volunteers_Needed: 12,
        Requirements: null,
      },
      {
        ID: 4,
        Title: 'Buddies',
        Description:
          'Be a one-on-one buddy for a guest throughout the evening. Dance, laugh, and make memories together.',
        Image_URL: null,
        Time: '5:00 PM',
        Sign_Up_URL: '#',
        Volunteers_Needed: 20,
        Requirements: 'Must be CPP Certified by March 25',
      },
      {
        ID: 5,
        Title: 'Check In & Welcome',
        Description:
          'Greet guests and families at the door, manage check-in, and hand out corsages and boutonnieres.',
        Image_URL: null,
        Time: '4:30 PM',
        Sign_Up_URL: '#',
        Volunteers_Needed: 5,
        Requirements: null,
      },
    ],
    Smart_Registration: null,
    Content_Slides: null,
    Card_Theme: null,
  },

  // ── Winter Retreat ──────────────────────────────────────────────────────
  {
    ID: 100,
    Title: 'Winter Retreat',
    Subtitle: 'Students',
    Detail_Heading: null,
    Description:
      'Get ready for one of the best trips of the year! Select your location to see student retreat dates and volunteer opportunities for your campus.',
    Page_URL: 'https://woodsidebible.org/student-winter-retreats/',
    BG_URL: '/Retreat/BGImage.png',
    BG_Is_Video: false,
    FG_URL: '/Retreat/ForegroundImage.png',
    Congregation_Name: 'All Campuses',
    Ministry_Name: 'Student Ministry',
    Start_Date: 'Feb 13, 2026',
    End_Date: 'Mar 1, 2026',
    Registration_URL: null,
    Registration_Label: null,
    Sessions: [],
    Serving_Roles: [],
    Registration_Options: null,
    Smart_Registration: {
      Groups: [
        {
          Label: 'Middle School',
          Description: 'For students in grades 6–8',
          Campus_Sessions: [
            {
              Campus: 'Lake Orion',
              Session_Label: 'Middle School Winter Retreat',
              Dates: 'February 13–15, 2026',
              Registration_URL: '#',
              Leader_URL: '#',
              Work_Crew_URL: '#',
            },
            {
              Campus: 'Troy',
              Session_Label: 'Middle School Winter Retreat',
              Dates: 'February 13–15, 2026',
              Registration_URL: '#',
              Leader_URL: '#',
              Work_Crew_URL: '#',
            },
            {
              Campus: 'Rochester',
              Session_Label: 'Middle School Winter Retreat',
              Dates: 'February 13–15, 2026',
              Registration_URL: '#',
              Leader_URL: '#',
              Work_Crew_URL: '#',
            },
          ],
        },
        {
          Label: 'High School',
          Description: 'For students in grades 9–12',
          Campus_Sessions: [
            {
              Campus: 'Lake Orion',
              Session_Label: 'High School Winter Retreat Wk 1',
              Dates: 'February 20–22, 2026',
              Registration_URL: '#',
              Leader_URL: '#',
              Work_Crew_URL: '#',
            },
            {
              Campus: 'Troy',
              Session_Label: 'High School Winter Retreat Wk 1',
              Dates: 'February 20–22, 2026',
              Registration_URL: '#',
              Leader_URL: '#',
              Work_Crew_URL: '#',
            },
            {
              Campus: 'Rochester',
              Session_Label: 'High School Winter Retreat Wk 2',
              Dates: 'February 27 – March 1, 2026',
              Registration_URL: '#',
              Leader_URL: '#',
              Work_Crew_URL: '#',
            },
          ],
        },
      ],
    },
    Content_Slides: null,
    Card_Theme: null,
  },
];

// ---------------------------------------------------------------------------
// Mock Data — Recurring Series
// ---------------------------------------------------------------------------

const MOCK_SERIES: RecurringSeries[] = [
  {
    ID: 500,
    Title: 'The Collective',
    Subtitle: 'Young Adults',
    Description:
      'A gathering for young adults (18–29) to worship, hear teaching, and build community. Join us every Sunday night.',
    Page_URL: 'https://woodsidebible.org/young-adults-ministry/',
    Image_URL: '/Collective/Image.jpg',
    Special_Image_URL: '/Collective/RetreatImage.png',
    Congregation_Name: 'Troy Campus',
    Ministry_Name: 'Young Adults',
    Schedule: 'Sundays · 6:00 PM',
    Upcoming_Dates: [
      {
        ID: 501,
        Date: 'Sun, Feb 22',
        Time: '6:00 PM',
        Topic: 'Week 3: Standing Firm',
        Series: 'Unshakable',
        Image_URL: null,
        Registration_URL: null,
      },
      {
        ID: 502,
        Date: 'Sun, Mar 1',
        Time: '6:00 PM',
        Topic: 'Week 4: Rooted Deep',
        Series: 'Unshakable',
        Image_URL: null,
        Registration_URL: null,
      },
      {
        ID: 503,
        Date: 'Mar 13–15',
        Time: 'All Weekend',
        Topic: 'Collective Winter Retreat',
        Series: null,
        Image_URL: '/Collective/RetreatImage.png',
        Registration_URL: '#',
      },
      {
        ID: 504,
        Date: 'Sun, Mar 22',
        Time: '6:00 PM',
        Topic: null,
        Series: null,
        Image_URL: null,
        Registration_URL: null,
      },
    ],
  },
  {
    ID: 600,
    Title: 'Membership Pathway',
    Subtitle: null,
    Description:
      'Discover what it means to be part of the Woodside family. This class covers our mission, beliefs, and how you can get connected. Open to anyone exploring membership.',
    Page_URL: 'https://woodsidebible.org/next-steps/',
    Image_URL: '/Membership-Pathway/Image.jpg',
    Special_Image_URL: null,
    Congregation_Name: 'All Campuses',
    Ministry_Name: 'Connections',
    Schedule: 'Monthly · Saturdays',
    Upcoming_Dates: [
      {
        ID: 601,
        Date: 'Sat, Mar 7',
        Time: '9:00 AM – 12:00 PM',
        Topic: 'Session 1: Our Story & Mission',
        Series: null,
        Image_URL: null,
        Registration_URL: '#',
      },
      {
        ID: 602,
        Date: 'Sat, Apr 4',
        Time: '9:00 AM – 12:00 PM',
        Topic: 'Session 2: Beliefs & Values',
        Series: null,
        Image_URL: null,
        Registration_URL: '#',
      },
      {
        ID: 603,
        Date: 'Sat, May 2',
        Time: '9:00 AM – 12:00 PM',
        Topic: 'Session 3: Getting Connected',
        Series: null,
        Image_URL: null,
        Registration_URL: '#',
      },
    ],
  },
];

// ---------------------------------------------------------------------------
// Mock Data — Standalone Serving Opportunities
// ---------------------------------------------------------------------------

const MOCK_STANDALONE: StandaloneOpportunity[] = [
  {
    ID: 7,
    Title: 'Kids Ministry',
    Description:
      'Love on the next generation by serving in our nursery, preschool, or elementary environments on Sunday mornings.',
    Image_URL: '/Ongoing-Serve-Opps/kids.jpg',
    Congregation_Name: 'All Campuses',
    Ministry_Name: 'Kids Ministry',
    Time: 'Ongoing · Sundays',
    Sign_Up_URL: '#',
    Volunteers_Needed: 12,
  },
  {
    ID: 8,
    Title: 'Student Ministry',
    Description:
      'Invest in middle and high school students as a small group leader, mentor, or event volunteer.',
    Image_URL: '/Ongoing-Serve-Opps/students.jpg',
    Congregation_Name: 'All Campuses',
    Ministry_Name: 'Student Ministry',
    Time: 'Ongoing · Wednesdays & Sundays',
    Sign_Up_URL: '#',
    Volunteers_Needed: 8,
  },
  {
    ID: 9,
    Title: 'Worship & Production',
    Description:
      'Use your musical or technical gifts to lead worship through vocals, instruments, sound, lighting, or video.',
    Image_URL: '/Ongoing-Serve-Opps/worshipServices.jpg',
    Congregation_Name: 'All Campuses',
    Ministry_Name: 'Worship Arts',
    Time: 'Ongoing · Sundays',
    Sign_Up_URL: '#',
    Volunteers_Needed: null,
  },
  {
    ID: 10,
    Title: 'Production Support',
    Description:
      'Run sound, cameras, lighting, and graphics to create an engaging worship experience each week.',
    Image_URL: '/Ongoing-Serve-Opps/productionSupport.jpg',
    Congregation_Name: 'All Campuses',
    Ministry_Name: 'Production',
    Time: 'Ongoing · Sundays',
    Sign_Up_URL: '#',
    Volunteers_Needed: 5,
  },
  {
    ID: 11,
    Title: 'Safety Team',
    Description:
      'Help keep our church family safe by serving on the parking, greeting, or security teams.',
    Image_URL: '/Ongoing-Serve-Opps/safetyTeam.jpg',
    Congregation_Name: 'All Campuses',
    Ministry_Name: 'Safety & Security',
    Time: 'Ongoing · Sundays',
    Sign_Up_URL: '#',
    Volunteers_Needed: 6,
  },
  {
    ID: 12,
    Title: 'Food Pantry',
    Description:
      'Sort, pack, and distribute food to families in need through our Forgotten Harvest partnership.',
    Image_URL: '/Ongoing-Serve-Opps/foodPantry.jpg',
    Congregation_Name: 'Troy Campus',
    Ministry_Name: 'Outreach',
    Time: 'Ongoing · Wednesdays',
    Sign_Up_URL: '#',
    Volunteers_Needed: 10,
  },
  {
    ID: 13,
    Title: 'Lead a Life Group',
    Description:
      'Facilitate a small group where people can grow in faith, build friendships, and support one another.',
    Image_URL: '/Ongoing-Serve-Opps/leadAGroup.jpg',
    Congregation_Name: 'All Campuses',
    Ministry_Name: 'Life Groups',
    Time: 'Ongoing · Flexible',
    Sign_Up_URL: '#',
    Volunteers_Needed: null,
  },
  {
    ID: 14,
    Title: 'Building Renovation',
    Description:
      'Help with hands-on renovation projects to improve and maintain our church facilities.',
    Image_URL: '/Ongoing-Serve-Opps/buildingReno.jpg',
    Congregation_Name: 'Troy Campus',
    Ministry_Name: 'Facilities',
    Time: 'Ongoing · Saturdays',
    Sign_Up_URL: '#',
    Volunteers_Needed: 15,
  },
  {
    ID: 15,
    Title: 'Landscaping Team',
    Description:
      'Keep our campus grounds looking great through seasonal landscaping and outdoor maintenance.',
    Image_URL: '/Ongoing-Serve-Opps/landscapingTeam.png',
    Congregation_Name: 'Troy Campus',
    Ministry_Name: 'Facilities',
    Time: 'Ongoing · Seasonal',
    Sign_Up_URL: '#',
    Volunteers_Needed: null,
  },
  {
    ID: 16,
    Title: 'Special Events',
    Description:
      'Help plan and execute church-wide events like conferences, outreach days, and community gatherings.',
    Image_URL: '/Ongoing-Serve-Opps/specialEvents.jpg',
    Congregation_Name: 'All Campuses',
    Ministry_Name: 'Events',
    Time: 'As Needed',
    Sign_Up_URL: '#',
    Volunteers_Needed: null,
  },
];

// ---------------------------------------------------------------------------
// Mock Data — Current Sermon Series
// ---------------------------------------------------------------------------

const MOCK_SERMON_SERIES: SermonSeries = {
  ID: 900,
  Title: 'Missio Dei',
  Description:
    "Your greatest mission is right in front of you. Will you decide to live it out? Our world is overflowing with objectives and end goals, but what if your purpose was already written on your identity? This January, we're debuting a new message series that will uncover the incredible story of God's global purpose and your place in it. Whether you're exploring faith or already following Jesus, Missio Dei (the mission of God) will explore how God calls us to bring justice, hope, and restoration to the ends of the earth.",
  BG_URL: '/Sermon-Series/SermonSeries.jpg',
  Vertical_BG_URL: '/Sermon-Series/VerticalBG.png',
  FG_URL: '/Sermon-Series/SermonSeriesForeground.png',
  Page_URL: '#',
  Sermons: [
    {
      ID: 901,
      Title: "God's Mission, God's Image",
      Date: 'Jan 4',
      Speaker: 'Jim Dahlke',
      Speaker_Image_URL: '/Sermon-Series/Pastor.png',
      Is_Current: false,
      Scripture: {
        Reference: 'Genesis 1:26-31',
        URL: 'https://www.bible.com/bible/59/GEN.1.ESV',
      },
      Big_Idea: 'God created humanity in His image to fill the earth with His glory.',
      Points: [
        {
          Text: "Humanity was given God's image.",
          Reference: 'vv. 26-27',
          URL: 'https://www.bible.com/bible/59/gen.1.26-27',
        },
        {
          Text: "Humanity was given God's blessing.",
          Reference: 'vv. 28a, 29-30',
          URL: 'https://www.bible.com/bible/59/gen.1.28-30',
        },
        {
          Text: "Humanity was given God's mission.",
          Reference: 'v. 28b, 31',
          URL: 'https://www.bible.com/bible/59/gen.1.28-31',
        },
      ],
      Location: null,
      Service_Times: null,
      Instructions: null,
      Notes_URL: '#',
      Watch_URL: '#',
      Badges: [],
    },
    {
      ID: 902,
      Title: 'Jesus: The Salvation of God',
      Date: 'Jan 12',
      Speaker: 'Denzel Wright',
      Speaker_Image_URL: '/Sermon-Series/Speaker.jpg',
      Is_Current: true,
      Scripture: null,
      Big_Idea: null,
      Points: null,
      Location: 'Civic Center Park\n2525 Joslyn Rd\nOrion Township, MI 48360',
      Service_Times: [{ Time: '10:00 AM', Day: 'Sunday', Amenities: ['baby', 'coffee'] }],
      Instructions: 'Park in the Civic Center Park lot off Joslyn Rd. Setup begins at 9:00 AM.',
      Notes_URL: '#',
      Watch_URL: '#',
      Badges: [
        { Label: 'Baptisms', Icon: 'droplet' },
        { Label: 'Church in the Park', Icon: 'map-pin' },
      ],
    },
    {
      ID: 903,
      Title: 'A Blessing to All Nations',
      Date: 'Jan 19',
      Speaker: 'Jim Dahlke',
      Speaker_Image_URL: '/Sermon-Series/Pastor.png',
      Is_Current: false,
      Scripture: null,
      Big_Idea: null,
      Points: null,
      Location: '2500 Joslyn Rd\nLake Orion, MI 48360',
      Service_Times: [
        { Time: '9:30 AM', Day: 'Sunday', Amenities: ['baby', 'hand', 'coffee'] },
        { Time: '11:00 AM', Day: 'Sunday', Amenities: ['baby', 'hand', 'coffee'] },
      ],
      Instructions: null,
      Notes_URL: '#',
      Watch_URL: null,
      Badges: [],
    },
    {
      ID: 904,
      Title: 'Saved for a Greater Purpose',
      Date: 'Jan 25',
      Speaker: 'Brad House',
      Speaker_Image_URL: '/Sermon-Series/AltPastor.jpg',
      Is_Current: false,
      Scripture: null,
      Big_Idea: null,
      Points: null,
      Location: '2500 Joslyn Rd\nLake Orion, MI 48360',
      Service_Times: [
        { Time: '8:30 AM', Day: 'Sunday', Amenities: ['baby', 'hand', 'coffee'] },
        { Time: '10:00 AM', Day: 'Sunday', Amenities: ['baby', 'hand', 'coffee'] },
        { Time: '11:30 AM', Day: 'Sunday', Amenities: ['baby'] },
      ],
      Instructions: null,
      Notes_URL: null,
      Watch_URL: null,
      Badges: [{ Label: 'New Service Times!', Icon: 'clock' }],
    },
    {
      ID: 905,
      Title: 'A Servant for the Nations',
      Date: 'Feb 1',
      Speaker: null,
      Speaker_Image_URL: null,
      Is_Current: false,
      Scripture: null,
      Big_Idea: null,
      Points: null,
      Location: '2500 Joslyn Rd\nLake Orion, MI 48360',
      Service_Times: [
        { Time: '8:30 AM', Day: 'Sunday', Amenities: ['baby', 'hand', 'coffee'] },
        { Time: '10:00 AM', Day: 'Sunday', Amenities: ['baby', 'hand', 'coffee'] },
        { Time: '11:30 AM', Day: 'Sunday', Amenities: ['baby'] },
      ],
      Instructions: null,
      Notes_URL: null,
      Watch_URL: null,
      Badges: [{ Label: 'RSVP Today', Icon: 'calendar-check' }],
    },
  ],
};

// ---------------------------------------------------------------------------
// Mock Data — Announcements
// ---------------------------------------------------------------------------

const MOCK_ANNOUNCEMENTS: Announcement[] = [
  {
    ID: 1000,
    Title: 'Giving Fuels\nTransformation',
    Subtitle: null,
    Description:
      'Your giving will impact you personally, make a difference at Woodside Bible Church, and take the Gospel around the world.',
    Image_URL: '/Announcements/Giving/BGVideo.mp4',
    BG_Is_Video: true,
    URL: 'https://giving.woodsidebible.org/',
    CTA_Label: 'Give Now',
    Secondary_URL: 'https://giving.woodsidebible.org/recurring',
    Secondary_Label: 'Recurring Giving',
    Slider_Slides: null,
    Card_Theme: {
      Body_BG: '#0a0a0a',
      Body_BG_Image: null,
      Title_Color: '#2fbfaf',
      Subtitle_Color: '#2fbfaf',
      Body_Color: 'rgba(255,255,255,0.8)',
      Accent_Color: '#2fbfaf',
      Border_Color: 'rgba(255,255,255,0.15)',
    },
  },
];

// ---------------------------------------------------------------------------
// Mock Data — Baptism
// ---------------------------------------------------------------------------

const MOCK_BAPTISM: BaptismInfo = {
  Tagline: 'An outward expression of the change that has taken place inside you.',
  Description:
    "Baptism is an outward expression of the change that's taken place inside you once you've placed your faith in Jesus. It's an important step of obedience that symbolizes dying to your old self and rising again to new life in Christ. Ready to take this step in your faith journey? Let us know today by signing up below.",
  Hero_Image_URL: '/Baptism/hero.jpg',
  Inquiry_URL: 'https://woodsidebible.org/baptism/',
  FAQ: [
    {
      Question: 'Why baptism by immersion?',
      Answer:
        "Baptism by immersion is a symbol of Jesus' death and resurrection. When we go under water, we identify with Christ's death and burial. When we come out of the water, we symbolize Christ's resurrection. Just as He rose from the dead, we now walk in a \"newness\" of life. (Romans 6:4,2; 2 Corinthians 5:17)",
    },
    {
      Question: 'Does baptism save me?',
      Answer:
        'Baptism is not a requirement for salvation, but a step of obedience as you follow Christ after salvation. (Romans 10:9) Salvation is by faith in Jesus Christ alone. (John 3:16, Acts 16:31, Ephesians 2:8-9)',
    },
    {
      Question: 'What should I expect?',
      Answer:
        'Baptisms happen during weekend services. You will briefly share your story of faith, then be immersed in water by one of our pastors. We provide towels and a changing area. Bring a change of clothes and invite your friends and family to celebrate with you!',
    },
    {
      Question: 'Who can be baptized?',
      Answer:
        'Anyone who has made the decision to follow Jesus! There is no age requirement — we just ask that you understand the significance of what baptism represents. If you have questions, reach out and we will walk through it with you.',
    },
  ],
  Upcoming_Dates: [
    {
      ID: 1100,
      Date: 'Sun, Mar 8',
      Time: '10:00 AM',
      Congregation_Name: 'Troy',
      Context: 'During Sunday Service',
      Sign_Up_URL: '#',
    },
    {
      ID: 1101,
      Date: 'Sun, Mar 22',
      Time: '10:00 AM',
      Congregation_Name: 'Lake Orion',
      Context: 'During Sunday Service',
      Sign_Up_URL: '#',
    },
    {
      ID: 1102,
      Date: 'Sun, Apr 5',
      Time: '10:00 AM',
      Congregation_Name: 'Rochester',
      Context: 'During Sunday Service',
      Sign_Up_URL: '#',
    },
    {
      ID: 1103,
      Date: 'Sun, Jun 14',
      Time: '10:00 AM',
      Congregation_Name: 'All Campuses',
      Context: 'Church in the Park',
      Sign_Up_URL: '#',
    },
  ],
};

// ---------------------------------------------------------------------------
// Mock Data — I'm New
// ---------------------------------------------------------------------------

const MOCK_IM_NEW: ImNewInfo = {
  Tagline: 'Welcome to more.',
  Description:
    "There's more to life than your good deeds or successes, your regrets or mistakes. At Woodside, we are family. No matter where you've come from, you belong. Let's find it together.",
  Hero_Image_URL: '/Announcements/NewHere/hero.jpg',
  Page_URL: 'https://woodsidebible.org/im-new/',
  Sections: [
    {
      Label: 'Gather in Worship',
      Description: 'Find a campus near you and join us Sunday',
      Image_URL: '/Announcements/NewHere/worship.jpg',
      URL: 'https://woodsidebible.org/locations/',
    },
    {
      Label: 'Get Connected',
      Description: 'Take your next step and meet your church family',
      Image_URL: '/Announcements/NewHere/hero.jpg',
      URL: 'https://woodsidebible.org/connect/',
    },
    {
      Label: 'Find a Group',
      Description: 'Join a Life Group and find belonging in community',
      Image_URL: '/Announcements/NewHere/belonging.jpg',
      URL: 'https://woodsidebible.org/group-finder/',
    },
    {
      Label: 'Spread Hope',
      Description: 'Serve your neighbors locally and globally',
      Image_URL: '/Announcements/NewHere/comehome.jpg',
      URL: 'https://woodsidebible.org/outreach/',
    },
  ],
  Slider_Slides: [
    {
      Image_URL: '/Announcements/NewHere/hero.jpg',
      Heading: 'welcome to more',
      Blurb:
        "There's more to life than your good deeds or successes, your regrets or mistakes. The answer is right in front of you.",
    },
    {
      Image_URL: '/Announcements/NewHere/worship.jpg',
      Heading: 'gather in worship',
      Blurb:
        "Each Sunday, our Woodside Family gathers to learn from God's Word, worship Him, and unite in prayer.",
    },
    {
      Image_URL: '/Announcements/NewHere/belonging.jpg',
      Heading: 'find belonging',
      Blurb:
        'God created us for community, to know and be known by others. Join a Group and say hello to your church family.',
    },
    {
      Image_URL: '/Announcements/NewHere/comehome.jpg',
      Heading: 'come home',
      Blurb:
        "At Woodside, we are family. No matter where you've come from, you belong. Let's find it together.",
    },
  ],
};

// ---------------------------------------------------------------------------
// Mock Data — Life Groups
// ---------------------------------------------------------------------------

const MOCK_LIFE_GROUPS: LifeGroupsInfo = {
  Tagline: 'Life is better in community.',
  Description:
    'Life Groups are where real community happens at Woodside. Whether you want to grow in your faith, find support, or simply connect with others, there is a group for you. Browse by category or find a group near you.',
  Hero_Image_URL: '/Announcements/JoinAGroup/BGImage.png',
  Find_URL: 'https://woodsidebible.org/groups/',
  Featured_Group: {
    Group_Name: "Johnson's Life Group",
    Leaders: 'Mike & Sarah Johnson',
    Location: '1247 Maple Dr, Troy, MI',
    Next_Meeting: 'Wed, Feb 26',
    Time: '7:00 PM',
    Icon: 'users',
    Meeting_Actions: [
      {
        Label: 'Go',
        Description: 'Open in Maps',
        Icon: 'map-pin',
        URL: 'https://maps.google.com/?q=1247+Maple+Dr+Troy+MI',
      },
      {
        Label: 'Take Attendance',
        Description: 'Mark who showed up',
        Icon: 'user-check',
        URL: null,
      },
      {
        Label: 'Prayer Requests',
        Description: 'Share and view requests',
        Icon: 'heart',
        URL: null,
      },
      {
        Label: 'Homework',
        Description: "This week's study guide",
        Icon: 'book-open',
        URL: null,
      },
      {
        Label: 'Food Sign Up',
        Description: 'Sign up to bring food',
        Icon: 'utensils',
        URL: null,
      },
      {
        Label: 'Group Chat',
        Description: 'Message your group',
        Icon: 'message-circle',
        URL: null,
      },
    ],
  },
  Conference: {
    Title: 'Groups Conference',
    Description:
      'A night to cast vision for community at Woodside. Hear stories, meet leaders, and find your group.',
    Date: 'Fri, Mar 14 · 7:00 PM',
    Image_URL: '/Announcements/JoinAGroup/GroupsConference.png',
    Registration_URL: '#',
  },
  Categories: [
    {
      ID: 1,
      Label: "Men's Groups",
      Description:
        'Iron sharpens iron. Connect with other men pursuing faith, integrity, and brotherhood.',
      Schedule: 'Wednesdays · 7:00 PM',
      Next_Meeting: 'Wed, Feb 26',
      Icon: 'users',
      Browse_URL: 'https://woodsidebible.org/groups/?category=mens',
      Image_URL: '/Announcements/JoinAGroup/BGImage.png',
    },
    {
      ID: 2,
      Label: "Women's Groups",
      Description:
        'Build lasting friendships and grow together through Bible study, prayer, and encouragement.',
      Schedule: 'Tuesdays · 9:30 AM',
      Next_Meeting: 'Tue, Feb 25',
      Icon: 'heart',
      Browse_URL: 'https://woodsidebible.org/groups/?category=womens',
      Image_URL: '/Announcements/JoinAGroup/BGImage.png',
    },
    {
      ID: 3,
      Label: 'Young Adults',
      Description:
        'For ages 18–29. Navigate life after high school with Christ-centered community.',
      Schedule: 'Sundays · 6:00 PM',
      Next_Meeting: 'Sun, Feb 23',
      Icon: 'sparkles',
      Browse_URL: 'https://woodsidebible.org/groups/?category=young-adults',
      Image_URL: '/Announcements/JoinAGroup/BGImage.png',
    },
    {
      ID: 4,
      Label: 'Couples',
      Description: 'Strengthen your marriage and connect with other couples doing life together.',
      Schedule: 'Fridays · 7:00 PM',
      Next_Meeting: 'Fri, Feb 28',
      Icon: 'gem',
      Browse_URL: 'https://woodsidebible.org/groups/?category=couples',
      Image_URL: '/Announcements/JoinAGroup/BGImage.png',
    },
    {
      ID: 5,
      Label: 'Open Groups',
      Description:
        'Open to anyone — come as you are. Great for newcomers or anyone exploring faith.',
      Schedule: 'Various days & times',
      Next_Meeting: 'Wed, Feb 26',
      Icon: 'globe',
      Browse_URL: 'https://woodsidebible.org/groups/?category=open',
      Image_URL: '/Announcements/JoinAGroup/BGImage.png',
    },
  ],
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface ServeFinderPageProps {
  /** Pass featured flag from widget config / data attribute. Falls back to ?featured=true URL param. */
  featured?: boolean;
}

export function ServeFinderPage({ featured: featuredProp }: ServeFinderPageProps = {}) {
  const [events] = useState<ChurchEvent[]>(MOCK_EVENTS);
  const [series] = useState<RecurringSeries[]>(MOCK_SERIES);
  const [standaloneOpps] = useState<StandaloneOpportunity[]>(MOCK_STANDALONE);
  const [sermonSeries] = useState<SermonSeries | null>(MOCK_SERMON_SERIES);
  const [announcements] = useState<Announcement[]>(MOCK_ANNOUNCEMENTS);
  const [baptism] = useState<BaptismInfo | null>(MOCK_BAPTISM);
  const [lifeGroups] = useState<LifeGroupsInfo | null>(MOCK_LIFE_GROUPS);
  const [imNew] = useState<ImNewInfo | null>(MOCK_IM_NEW);
  const [isLoading] = useState(false);
  const featured = featuredProp ?? true;

  return (
    <div className="space-y-6">
      <header className="min-w-0">
        <SectionHeader
          title={"What's Happening"}
          subtitle={<RotatingSubtitle initialDelay={1400} />}
          icon={WoodsideIcon}
          variant="watermark"
          className="mb-0"
        />
      </header>

      <EventGrid
        events={events}
        series={series}
        standaloneOpps={standaloneOpps}
        sermonSeries={sermonSeries}
        announcements={announcements}
        baptism={baptism}
        lifeGroups={lifeGroups}
        imNew={imNew}
        isLoading={isLoading}
        featured={featured}
      />
    </div>
  );
}
